import React, {
	useState,
	useCallback,
	useMemo,
	useRef,
	useEffect,
} from 'react';
import gql from 'graphql-tag';
import {
	Button,
	Input,
	Select,
	DatePicker,
	Checkbox,
	Progress,
	Avatar,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import {
	FilterOutlined,
	DownloadOutlined,
	EditOutlined,
	CaretUpOutlined,
	CaretDownOutlined,
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'motion/react';
import EmployeeDetailsModalContainer from './EmployeeDetailsModalContainer';
import dayjs from 'dayjs';
import { downloadFromS3Signed } from 'src/_shared/services/utils';
import { FixedSizeList as List } from 'react-window';
import ResizeObserver from 'rc-resize-observer';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Role color mapping
const ROLE_COLORS = {
	admin: {
		normal: 'oklch(0.4 0.03 260.87 / 1)',
		lighter: 'oklch(0.80 0.2 260.87 / 1)',
		lightest: 'oklch(0.98 0.1 276 / 1)',
	},
	manager: {
		normal: 'oklch(0.61 0.18 288.79)',
		lighter: 'oklch(0.81 0.18 288.79 / 1)',
		lightest: 'oklch(0.9323 0.0387 295)',
	},
	employee: {
		normal: 'oklch(0.62 0.15 205 / 1)',
		lighter: 'oklch(0.7903 0.136 192.97)',
		lightest: 'oklch(0.9823 0.0238 192.97)',
	},
	extendedUser: {
		normal: 'oklch(0.68 0.19 349.99)',
		lighter: 'oklch(0.88 0.19 349.99 / 1)',
		lightest: 'oklch(0.98 0.02 349.99 / 1)',
	},
	superAdmin: {
		normal: 'oklch(0.43 0.14 25.27)',
		lighter: 'oklch(0.83 0.14 25.27 / 1)',
		lightest: 'oklch(0.98 0.13 20 / 1)',
	},
	supportAdmin: {
		normal: 'oklch(0.6144 0.1809 25.93)',
		lighter: 'oklch(0.8352 0.1174 25.93)',
		lightest: 'oklch(0.95 0.0316 25.93)',
	},
};

const nameToColor = (name) => {
	if (!name || name.trim().length === 0) return 'oklch(0.6144 0.1809 25.93)';
	const colors = [
		'oklch(0.6144 0.1809 25.93)', // Lighter ERIN red
		'oklch(0.39 0.159 24.91)', // Darker ERIN red
		'oklch(0.2998 0.034 260.85)', // Dark ERIN blue
		'oklch(0.58 0.26 295)', // Recognition Purple
		'oklch(0.7903 0.136 192.97)', // Recognition Pink
		'oklch(0.6757 0.187 349.98)', // Recognition Light Blue
		'oklch(0.5307 0.1791 144.1)', // Dark Green
		'oklch(0.7099 0.1414 141.23)', // Lighter Green
	];

	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}

	const index = Math.abs(hash) % colors.length;
	return colors[index];
};

const parseLocation = (raw) => {
	if (!raw) return undefined;
	if (typeof raw === 'string') {
		try {
			return JSON.parse(raw);
		} catch {
			return { address: raw };
		}
	}
	return raw;
};

const formatLocation = (loc) => {
	if (!loc) return '';
	if (loc.isRemote) {
		const parts = [loc.city, loc.state, loc.country].filter(Boolean).join(', ');
		return parts ? `Remote · ${parts}` : 'Remote';
	}
	if (loc.address) return loc.address;
	const parts = [loc.city, loc.state, loc.country].filter(Boolean).join(', ');
	return parts || '';
};

// Custom Filter Dropdown Component
const CustomFilterDropdown = ({
	column,
	filterModel,
	onFilter,
	onClose,
	multiSelectOptions,
}) => {
	const [operation, setOperation] = useState('eq');
	const [filterValue, setFilterValue] = useState('');
	const [isCaseSensitive, setIsCaseSensitive] = useState(true);
	const [dateRange, setDateRange] = useState(null);
	const [singleDate, setSingleDate] = useState(null);

	const arrayFilters = ['role', 'departmentId', 'userGroupId', 'department'];
	const arrayLabelsMap = {
		role: 'Choose Role(s)',
		departmentId: 'Choose Department(s)',
		department: 'Choose Department(s)',
		userGroupId: 'Choose User Group(s)',
	};

	useEffect(() => {
		const initialFilterData = filterModel?.[column.dataIndex];
		if (initialFilterData) {
			if (arrayFilters.includes(column.dataIndex)) {
				setFilterValue(initialFilterData.map((f) => f.eq) || []);
			} else if (column.dataType === 'date' && initialFilterData.between) {
				setDateRange([
					dayjs(initialFilterData.between[0]),
					dayjs(initialFilterData.between[1]),
				]);
				setOperation('between');
			} else {
				const op = Object.keys(initialFilterData)[0];
				setOperation(op);
				setFilterValue(Object.values(initialFilterData)[0]);
				if (op === 'between') {
					setDateRange([
						dayjs(initialFilterData.between[0]),
						dayjs(initialFilterData.between[1]),
					]);
				} else if (column.dataType === 'date') {
					setSingleDate(dayjs(initialFilterData[op]));
				}
			}
		}
	}, [column, filterModel]);

	const handleApply = () => {
		let newFilter = null;
		if (operation && (filterValue || dateRange || singleDate)) {
			if (arrayFilters.includes(column.dataIndex)) {
				newFilter = filterValue.map((value) => ({ eq: value }));
			} else if (column.dataType === 'date') {
				if (operation === 'between' && dateRange) {
					newFilter = {
						between: [dateRange[0].toISOString(), dateRange[1].toISOString()],
					};
				} else if (singleDate) {
					newFilter = {
						[operation]: singleDate.toISOString(),
					};
				}
			} else {
				newFilter = {
					[operation]: filterValue,
				};
				if (column.dataType === 'text' && column.dataIndex !== 'employeeId') {
					newFilter.isCaseSensitive = isCaseSensitive;
				}
			}

			const filterFieldName =
				column.dataIndex === 'department' ? 'departmentId' : column.dataIndex;
			onFilter({
				...filterModel,
				[filterFieldName]: newFilter,
			});
		}
		onClose();
	};

	const handleClear = () => {
		const filterFieldName =
			column.dataIndex === 'department' ? 'departmentId' : column.dataIndex;
		onFilter({
			...filterModel,
			[filterFieldName]: null,
		});
		onClose();
	};

	const handleClearAll = () => {
		onFilter(null);
		onClose();
	};

	const renderFilterControls = () => {
		switch (column.dataType) {
			case 'text':
				return (
					<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						<div>
							<label
								style={{
									display: 'block',
									marginBottom: '4px',
									fontSize: '13px',
								}}
							>
								Condition
							</label>
							<Select
								value={operation}
								onChange={setOperation}
								style={{ width: '100%' }}
								size="small"
							>
								<Select.Option value="eq">Equal To</Select.Option>
								<Select.Option value="ne">Not Equal To</Select.Option>
								<Select.Option value="contains">Contains</Select.Option>
								<Select.Option value="notContains">
									Does Not Contain
								</Select.Option>
								<Select.Option value="beginsWith">Begins With</Select.Option>
							</Select>
						</div>
						<div>
							<label
								style={{
									display: 'block',
									marginBottom: '4px',
									fontSize: '12px',
								}}
							>
								Value
							</label>
							<Input
								value={filterValue}
								onChange={(e) => setFilterValue(e.target.value)}
								placeholder="Filter value"
								size="small"
								disabled={!operation}
							/>
						</div>
						{column.dataIndex !== 'employeeId' && (
							<div>
								<Checkbox
									checked={isCaseSensitive}
									onChange={(e) => setIsCaseSensitive(e.target.checked)}
								/>
								<span style={{ fontSize: '12px', marginLeft: '4px' }}>
									Case Sensitive
								</span>
							</div>
						)}
					</div>
				);
			case 'boolean':
				return (
					<div>
						<label
							style={{
								display: 'block',
								marginBottom: '4px',
								fontSize: '13px',
							}}
						>
							Status
						</label>
						<Select
							value={filterValue}
							onChange={setFilterValue}
							placeholder="Select status"
							style={{ width: '100%' }}
							size="small"
						>
							<Select.Option value={true}>Active</Select.Option>
							<Select.Option value={false}>Inactive</Select.Option>
						</Select>
					</div>
				);
			case 'multiSelect':
				return (
					<div>
						<label
							style={{
								display: 'block',
								marginBottom: '4px',
								fontSize: '13px',
							}}
						>
							{arrayLabelsMap[column.dataIndex]}
						</label>
						<Select
							mode="multiple"
							style={{ width: '100%' }}
							placeholder="Select options"
							value={filterValue}
							onChange={setFilterValue}
							options={multiSelectOptions}
						/>
					</div>
				);
			case 'date':
				return (
					<div>
						<label
							style={{
								display: 'block',
								marginBottom: '4px',
								fontSize: '13px',
							}}
						>
							Condition
						</label>
						<Select
							value={operation}
							onChange={(value) => {
								setOperation(value);
								if (value !== 'between') {
									setDateRange(null);
								} else {
									setSingleDate(null);
								}
							}}
							style={{ width: '100%' }}
							size="small"
						>
							<Select.Option value="le">Before</Select.Option>
							<Select.Option value="ge">After</Select.Option>
							<Select.Option value="between">Between</Select.Option>
						</Select>
						{operation === 'between' ? (
							<>
								<label
									style={{
										display: 'block',
										marginBottom: '4px',
										fontSize: '13px',
									}}
								>
									Date Range
								</label>
								<RangePicker
									value={dateRange}
									onChange={setDateRange}
									style={{ width: '100%' }}
									size="small"
								/>
							</>
						) : (
							<>
								<label
									style={{
										display: 'block',
										marginBottom: '4px',
										fontSize: '13px',
									}}
								>
									Date
								</label>
								<DatePicker
									value={singleDate}
									onChange={setSingleDate}
									style={{ width: '100%' }}
									size="small"
									disabled={!operation}
								/>
							</>
						)}
					</div>
				);
			default:
				return <p>No filter available for this column type.</p>;
		}
	};

	if (!visible) return null;

	return (
		<div
			style={{
				padding: '16px',
				minWidth: '220px',
				backgroundColor: '#fff',
				border: '1px solid #d9d9d9',
				borderRadius: '6px',
				boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
			}}
		>
			<div style={{ marginBottom: '12px' }}>
				<h4
					style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}
				>
					Filter: {column.title}
				</h4>
			</div>
			{renderFilterControls()}
			<div
				style={{
					marginTop: '16px',
					display: 'flex',
					justifyContent: 'space-between',
					gap: '8px',
				}}
			>
				<Button size="small" onClick={handleClear}>
					Clear
				</Button>
				<Button size="small" onClick={handleClearAll}>
					Clear All
				</Button>
				<Button size="small" onClick={onClose}>
					Cancel
				</Button>
				<Button type="primary" size="small" onClick={handleApply}>
					Apply
				</Button>
			</div>
		</div>
	);
};

// Export Progress Component
const AnimatedExportButton = ({
	onExportClick,
	exportLoading,
	percentageLoaded,
	disabled,
}) => {
	return (
		<AnimatePresence mode="wait">
			{percentageLoaded === 0 && !exportLoading ? (
				<motion.div
					key="export-button"
					initial={{ width: 'auto', scale: 1 }}
					exit={{ width: 0, opacity: 0, scale: 0.8 }}
				>
					<Button
						icon={<DownloadOutlined />}
						onClick={onExportClick}
						disabled={disabled}
					>
						Export CSV
					</Button>
				</motion.div>
			) : (
				<motion.div
					key="progress-bar"
					initial={{ width: 120, opacity: 0, scale: 0.9 }}
					animate={{ width: 300, opacity: 1, scale: 1 }}
					exit={{ opacity: 0 }}
				>
					<Progress
						style={{ width: '100%' }}
						strokeColor={{ from: '#108ee9', to: '#87d068' }}
						percent={percentageLoaded}
						status={
							exportLoading
								? 'active'
								: percentageLoaded === 100
									? 'success'
									: 'exception'
						}
					/>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

// GraphQL Queries
const QUERY_EMPLOYEES = gql`
	query QueryUsersByCompanyIdPostgres(
		$companyId: ID!
		$limit: Int
		$nextToken: String
		$sort: EmployeeSortInput
		$filter: EmployeeFilterInput
	) {
		queryUsersByCompanyIdPostgres(
			companyId: $companyId
			limit: $limit
			nextToken: $nextToken
			sort: $sort
			filter: $filter
		) {
			items {
				active
				emailAddress
				firstName
				id
				lastName
				role
				title
				userGroupId
				dateCreated
				departmentId
				employeeId
				additionalInfo {
					location
					subCompanyId
					recognitionEvents {
						currentBudget
					}
					avatarSignedUrl
					avatar {
						key
						bucket
						region
					}
				}
			}
			nextToken
			totalCount
		}
	}
`;

const QUERY_DEPARTMENTS = gql`
	query QueryDepartmentsByCompanyIdIndex(
		$companyId: ID!
		$first: Int
		$after: String
	) {
		queryDepartmentsByCompanyIdIndex(
			companyId: $companyId
			first: $first
			after: $after
		) {
			items {
				id
				name
				companyId
				keywords
				externalDepartmentId
				active
			}
			nextToken
		}
	}
`;

const QUERY_USERGROUPS = gql`
	query QueryUserGroupsByCompanyIdIndex(
		$companyId: ID!
		$first: Int
		$after: String
	) {
		queryUserGroupsByCompanyIdIndex(
			companyId: $companyId
			first: $first
			after: $after
		) {
			items {
				id
				active
				companyId
				currency
				keywords
				measurement
				name
				languageCode
			}
			nextToken
		}
	}
`;

// Main Component
const EmployeeGridAntD = ({
	companyId = '30abdd5d-5e53-4016-9495-7b7ec1444665',
	client,
	currentUser,
}) => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [exportLoading, setExportLoading] = useState(false);
	const [totalRecords, setTotalRecords] = useState(0);
	const [sortModel, setSortModel] = useState(null);
	const [filterModel, setFilterModel] = useState(null);
	const [nextTokenWithPage, setNextTokenWithPage] = useState({
		nextToken: null,
		page: 0,
	});
	const [modalVisible, setModalVisible] = useState(false);
	const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
	const [userGroups, setUserGroups] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [pageSize] = useState(50);
	const [currentPage, setCurrentPage] = useState(0);
	const [percentageLoaded, setPercentageLoaded] = useState(0);
	const [filterDropdowns, setFilterDropdowns] = useState({});
	const adminPermissions = currentUser?.admin
		? Boolean(JSON.parse(currentUser.admin)?.permissions === '*')
		: false;

	// Admin feature: Copy user ID to clipboard
	const handleCopyUserId = useCallback(
		async (userId, userName) => {
			if (!adminPermissions) return;

			try {
				await navigator.clipboard.writeText(userId);
				// Simple notification - you could enhance this with a toast library
				const notification = document.createElement('div');
				notification.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                background: #52c41a;
                color: white;
                padding: 12px 16px;
                border-radius: 6px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                z-index: 10000;
                font-size: 14px;
                font-weight: 500;
            `;
				notification.textContent = `✓ User ID copied: ${userName}`;
				document.body.appendChild(notification);

				setTimeout(() => {
					if (document.body.contains(notification)) {
						document.body.removeChild(notification);
					}
				}, 2000);
			} catch (error) {
				console.error('Failed to copy user ID:', error);
				// Fallback for older browsers
				const textArea = document.createElement('textarea');
				textArea.value = userId;
				document.body.appendChild(textArea);
				textArea.select();
				document.execCommand('copy');
				document.body.removeChild(textArea);

				alert(`User ID copied: ${userId}`);
			}
		},
		[adminPermissions]
	);
	// Avatar cache system for performance
	const avatarCache = useRef(new Map());
	const MAX_CACHE_SIZE = 1000; // Prevent memory leaks

	// Clear cache on unmount to prevent memory leaks
	useEffect(() => {
		return () => {
			avatarCache.current.clear();
		};
	}, []);

	const roleOptions = [
		{ label: 'Admin', value: 'admin' },
		{ label: 'Manager', value: 'manager' },
		{ label: 'Employee', value: 'employee' },
		{ label: 'Extended User', value: 'extendedUser' },
		{ label: 'Super Admin', value: 'superAdmin' },
		{ label: 'Support Admin', value: 'supportAdmin' },
	];
	const roleMap = {
		admin: 'Admin',
		manager: 'Manager',
		employee: 'Employee',
		extendedUser: 'Extended User',
		superAdmin: 'Super Admin',
		supportAdmin: 'Support Admin',
		superUser: 'Super User',
	};

	const hasMoreData = totalRecords > data.length;

	// Fetch data function
	const fetchData = useCallback(
		async ({ page = 0, resetData = true, nextTokenArg = null }) => {
			try {
				setLoading(true);

				const queryVariables = {
					companyId,
					limit: pageSize,
				};

				if (nextTokenArg) {
					queryVariables.nextToken = nextTokenArg;
				}

				if (sortModel) {
					queryVariables.sort = sortModel;
				}

				if (filterModel) {
					queryVariables.filter = filterModel;
				}

				const { data: responseData } = await client.query({
					query: QUERY_EMPLOYEES,
					variables: queryVariables,
					fetchPolicy: 'network-only',
				});

				const { items, totalCount, nextToken } =
					responseData.queryUsersByCompanyIdPostgres;

				const safeDownloadFromS3Signed = async (key, fullSize = false) => {
					const cacheKey = `${key}_${fullSize ? 'full' : 'thumb'}`;

					// Check cache first - return cached URL if exists
					if (avatarCache.current.has(cacheKey)) {
						return avatarCache.current.get(cacheKey);
					}

					try {
						let signedUrl;
						if (fullSize) {
							signedUrl = await downloadFromS3Signed(key, 'erin-avatars');
						} else {
							// Fix double-thumb bug: only replace if not already a thumb
							const thumbKey = key.includes('user-pic-thumb')
								? key
								: key.replace('user-pic', 'user-pic-thumb');
							signedUrl = await downloadFromS3Signed(thumbKey, 'erin-avatars');
						}

						// Cache the signed URL with size limit
						if (avatarCache.current.size >= MAX_CACHE_SIZE) {
							// Remove oldest entries (FIFO)
							const firstKey = avatarCache.current.keys().next().value;
							avatarCache.current.delete(firstKey);
						}
						avatarCache.current.set(cacheKey, signedUrl);

						return signedUrl;
					} catch (error) {
						console.warn('Error downloading avatar:', error);
						const fallbackUrl = fullSize ? '' : '';

						// Cache fallback too to prevent repeated failures
						if (avatarCache.current.size < MAX_CACHE_SIZE) {
							avatarCache.current.set(cacheKey, fallbackUrl);
						}

						return fallbackUrl;
					}
				};

				const rowPromises = items.map(async (i) => {
					let avatarUrl = '';
					let avatarThumbUrl = '';
					let avatarSignedUrl = i?.additionalInfo?.avatarSignedUrl || '';

					// if (i.additionalInfo?.avatar?.key) {
					// 	try {
					// 		avatarUrl = await safeDownloadFromS3Signed(
					// 			i.additionalInfo.avatar.key,
					// 			true
					// 		);
					// 		avatarThumbUrl = await safeDownloadFromS3Signed(
					// 			i.additionalInfo.avatar.key.replace(
					// 				'user-pic',
					// 				'user-pic-thumb'
					// 			),
					// 			false
					// 		);
					// 	} catch (error) {
					// 		console.error('Error loading avatar:', error);
					// 	}
					// }

					const rawLocation = i.additionalInfo?.location;
					const locationObject = parseLocation(rawLocation);

					return {
						...i,
						key: i.id,
						locationObject: locationObject,
						location: formatLocation(locationObject),
						budget:
							'$' +
							(i.additionalInfo?.recognitionEvents?.currentBudget ||
								currentUser?.company?.recognitionSettings?.baseSnappyBudget ||
								'0'),
						avatar: avatarSignedUrl, //avatarUrl,
						avatarThumbnail: avatarSignedUrl, //avatarThumbUrl,
					};
				});

				const resolvedRows = await Promise.all(rowPromises);

				if (resetData || page === 0) {
					setData(resolvedRows);
				} else {
					// Deduplicate by employee ID to prevent duplicate records
					setData((prevData) => {
						const existingIds = new Set(prevData.map((item) => item.id));
						const newRows = resolvedRows.filter(
							(row) => !existingIds.has(row.id)
						);
						const combinedData = [...prevData, ...newRows];

						// Ensure we don't exceed totalRecords
						if (totalRecords && combinedData.length > totalRecords) {
							return combinedData.slice(0, totalRecords);
						}

						return combinedData;
					});
				}

				setNextTokenWithPage({
					nextToken: nextToken || null,
					page: page + 1,
				});

				if (page === 0) {
					setTotalRecords(totalCount || 0);
				}

				return resolvedRows;
			} catch (err) {
				console.error('Error fetching employees:', err);
				setData([]);
				setTotalRecords(0);
				return [];
			} finally {
				setLoading(false);
			}
		},
		[client, companyId, currentUser, filterModel, sortModel, pageSize]
	);

	// Virtualization state
	const [tableWidth, setTableWidth] = useState(1200);
	const [tableHeight, setTableHeight] = useState(600);
	const listRef = useRef();
	const ROW_HEIGHT = 60;

	// Column resizing state
	const [columnWidths, setColumnWidths] = useState({
		actions: 80,
		employee: 280,
		email: 220,
		budget: 100,
		role: 120,
		department: 180,
		employeeId: 120,
		dateCreated: 140,
		status: 100,
		userGroup: 150,
	});
	const [isResizing, setIsResizing] = useState(false);
	const [resizeColumnKey, setResizeColumnKey] = useState(null);
	const [startX, setStartX] = useState(0);
	const [startWidth, setStartWidth] = useState(0);

	// Sorting handlers
	const handleSort = useCallback(
		(field) => {
			const currentDirection =
				sortModel?.field === field ? sortModel.direction : null;
			let newDirection;

			if (!currentDirection) {
				newDirection = 'ASC';
			} else if (currentDirection === 'ASC') {
				newDirection = 'DESC';
			} else {
				newDirection = null; // Reset sorting
			}

			if (newDirection) {
				setSortModel({ field, direction: newDirection });
			} else {
				setSortModel(null);
			}
		},
		[sortModel, setSortModel]
	);

	// Sort icon component - Ant Design v4 style (separate carets like real Ant Design)
	const SortIcon = React.memo(({ field }) => {
		const isActive = sortModel?.field === field;
		const direction = isActive ? sortModel.direction : null;

		return (
			<span
				className="ant-table-column-sorter ant-table-column-sorter-full"
				style={{ marginLeft: 4, fontSize: 11 }}
			>
				<span className="ant-table-column-sorter-inner">
					<span
						className="ant-table-column-sorter-up"
						style={{
							color:
								isActive && direction === 'ASC'
									? '#1890ff'
									: 'rgba(0, 0, 0, 0.29)',
							marginTop: '-0.3em',
							verticalAlign: '-0.125em',
						}}
					>
						<CaretUpOutlined />
					</span>
					<span
						className="ant-table-column-sorter-down"
						style={{
							color:
								isActive && direction === 'DESC'
									? '#1890ff'
									: 'rgba(0, 0, 0, 0.29)',
							marginTop: '-0.6em',
						}}
					>
						<CaretDownOutlined />
					</span>
				</span>
			</span>
		);
	});

	const [activeFilterDropdown, setActiveFilterDropdown] = useState(null);

	const FilterDropdown = React.memo(({ field, dataType, options = null }) => {
		const [textValue, setTextValue] = useState('');
		const [textOperator, setTextOperator] = useState('contains');
		const [caseSensitive, setCaseSensitive] = useState(false);

		const [selectedValues, setSelectedValues] = useState([]);

		const [dateOperator, setDateOperator] = useState('between');
		const [startDate, setStartDate] = useState(null);
		const [endDate, setEndDate] = useState(null);
		const [singleDate, setSingleDate] = useState(null);

		const [booleanValue, setBooleanValue] = useState('active');

		const [firstNameValue, setFirstNameValue] = useState('');
		const [firstNameOperator, setFirstNameOperator] = useState('contains');
		const [firstNameCaseSensitive, setFirstNameCaseSensitive] = useState(false);

		const [lastNameValue, setLastNameValue] = useState('');
		const [lastNameOperator, setLastNameOperator] = useState('contains');
		const [lastNameCaseSensitive, setLastNameCaseSensitive] = useState(false);

		const [titleValue, setTitleValue] = useState('');
		const [titleOperator, setTitleOperator] = useState('contains');
		const [titleCaseSensitive, setTitleCaseSensitive] = useState(false);

		useEffect(() => {
			if (activeFilterDropdown === field && filterModel) {
				if (field === 'employee') {
					const firstNameFilter = filterModel.firstName;
					const lastNameFilter = filterModel.lastName;
					const titleFilter = filterModel.title;

					if (firstNameFilter) {
						const operator = Object.keys(firstNameFilter)[0];
						setFirstNameOperator(
							operator === 'isCaseSensitive' ? 'contains' : operator
						);
						setFirstNameValue(firstNameFilter[operator] || '');
						setFirstNameCaseSensitive(firstNameFilter.isCaseSensitive ?? false);
					}

					if (lastNameFilter) {
						const operator = Object.keys(lastNameFilter)[0];
						setLastNameOperator(
							operator === 'isCaseSensitive' ? 'contains' : operator
						);
						setLastNameValue(lastNameFilter[operator] || '');
						setLastNameCaseSensitive(lastNameFilter.isCaseSensitive ?? false);
					}

					if (titleFilter) {
						const operator = Object.keys(titleFilter)[0];
						setTitleOperator(
							operator === 'isCaseSensitive' ? 'contains' : operator
						);
						setTitleValue(titleFilter[operator] || '');
						setTitleCaseSensitive(titleFilter.isCaseSensitive ?? false);
					}
				} else if (dataType === 'text') {
					const currentFilter = filterModel[field];
					if (currentFilter) {
						const operators = Object.keys(currentFilter).filter(
							(k) => k !== 'isCaseSensitive'
						);
						if (operators.length > 0) {
							const operator = operators[0];
							setTextOperator(operator);
							setTextValue(currentFilter[operator] || '');
							setCaseSensitive(currentFilter.isCaseSensitive ?? false);
						}
					}
				} else if (dataType === 'multiSelect') {
					const currentFilter = filterModel[field] || filterModel.departmentId;
					if (currentFilter && Array.isArray(currentFilter)) {
						const values = currentFilter.map((item) => item.eq).filter(Boolean);
						setSelectedValues(values);
					}
				} else if (dataType === 'boolean') {
					const currentFilter = filterModel.active;
					if (currentFilter) {
						setBooleanValue(currentFilter.eq === true ? 'active' : 'inactive');
					}
				} else if (dataType === 'date') {
					const currentFilter = filterModel[field];
					if (currentFilter) {
						if (currentFilter.between) {
							setDateOperator('between');
							setStartDate(dayjs(currentFilter.between[0]));
							setEndDate(dayjs(currentFilter.between[1]));
							setSingleDate(null);
						} else if (currentFilter.le) {
							setDateOperator('le');
							setSingleDate(dayjs(currentFilter.le));
							setStartDate(null);
							setEndDate(null);
						} else if (currentFilter.ge) {
							setDateOperator('ge');
							setSingleDate(dayjs(currentFilter.ge));
							setStartDate(null);
							setEndDate(null);
						}
					}
				}
			}
		}, [activeFilterDropdown, field, filterModel, dataType]);

		const textOperatorOptions = [
			{ label: 'Contains', value: 'contains' },
			{ label: 'Does not contain', value: 'notContains' },
			{ label: 'Equals', value: 'eq' },
			{ label: 'Not equals', value: 'ne' },
			{ label: 'Begins with', value: 'beginsWith' },
		];

		const dateOperatorOptions = [
			{ label: 'Between', value: 'between' },
			{ label: 'Before or on', value: 'le' },
			{ label: 'After or on', value: 'ge' },
		];

		const buildTextFilter = (value, operator, isCaseSensitive, fieldName) => {
			if (!value) return null;

			const filter = { [operator]: value };
			if (fieldName !== 'employeeId') {
				filter.isCaseSensitive = isCaseSensitive;
			}
			return filter;
		};

		const buildDateFilter = (operator, start, end, single) => {
			if (operator === 'between' && start && end) {
				return {
					between: [
						start.startOf('day').toISOString(),
						end.endOf('day').toISOString(),
					],
				};
			} else if (operator === 'le' && single) {
				return { le: single.endOf('day').toISOString() };
			} else if (operator === 'ge' && single) {
				return { ge: single.startOf('day').toISOString() };
			}
			return null;
		};

		const buildMultiSelectFilter = (values) => {
			if (!values || values.length === 0) return null;
			return values.map((value) => ({ eq: value }));
		};

		const handleApplyFilter = () => {
			if (field === 'employee') {
				const firstNameFilter = buildTextFilter(
					firstNameValue,
					firstNameOperator,
					firstNameCaseSensitive,
					'firstName'
				);
				const lastNameFilter = buildTextFilter(
					lastNameValue,
					lastNameOperator,
					lastNameCaseSensitive,
					'lastName'
				);
				const titleFilter = buildTextFilter(
					titleValue,
					titleOperator,
					titleCaseSensitive,
					'title'
				);

				handleFilter('firstName', firstNameFilter);
				handleFilter('lastName', lastNameFilter);
				handleFilter('title', titleFilter);
			} else if (dataType === 'text') {
				const filter = buildTextFilter(
					textValue,
					textOperator,
					caseSensitive,
					field
				);
				handleFilter(field, filter);
			} else if (dataType === 'multiSelect') {
				const filter = buildMultiSelectFilter(selectedValues);
				handleFilter(field, filter);
			} else if (dataType === 'date') {
				const filter = buildDateFilter(
					dateOperator,
					startDate,
					endDate,
					singleDate
				);
				handleFilter(field, filter);
			} else if (dataType === 'boolean') {
				const filter = booleanValue === 'active' ? { eq: true } : { ne: true };
				handleFilter('active', filter);
			}

			setActiveFilterDropdown(null);
		};

		const handleClearFilter = () => {
			if (field === 'employee') {
				setFirstNameValue('');
				setLastNameValue('');
				setTitleValue('');
				handleFilter('firstName', null);
				handleFilter('lastName', null);
				handleFilter('title', null);
			} else {
				setTextValue('');
				setSelectedValues([]);
				setStartDate(null);
				setEndDate(null);
				setSingleDate(null);
				setBooleanValue('active');

				if (dataType === 'boolean') {
					handleFilter('active', null);
				} else {
					handleFilter(field, null);
				}
			}
			setActiveFilterDropdown(null);
		};

		if (activeFilterDropdown !== field) return null;

		return (
			<div
				className="filter-dropdown-content"
				style={{
					position: 'absolute',
					top: '100%',
					left: 0,
					zIndex: 1000,
					backgroundColor: '#fff',
					border: '1px solid #d9d9d9',
					borderRadius: '6px',
					padding: '12px',
					minWidth: '280px',
					color: 'rgba(16, 16, 16, 0.83)',
					boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
				}}
			>
				{field === 'employee' ? (
					<div>
						{/* First Name Filter */}
						<div style={{ marginBottom: '12px' }}>
							<Typography.Text strong style={{ fontSize: '12px' }}>
								First Name:
							</Typography.Text>
							<div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
								<Select
									value={firstNameOperator}
									onChange={setFirstNameOperator}
									options={textOperatorOptions}
									style={{ width: '120px' }}
									size="small"
									getPopupContainer={(trigger) => trigger.parentNode}
								/>
								<Input
									placeholder="Value"
									value={firstNameValue}
									onChange={(e) => setFirstNameValue(e.target.value)}
									size="small"
									style={{ flex: 1 }}
								/>
							</div>
							<Checkbox
								checked={firstNameCaseSensitive}
								onChange={(e) => setFirstNameCaseSensitive(e.target.checked)}
								style={{ marginTop: '4px', fontSize: '12px' }}
							>
								Case sensitive
							</Checkbox>
						</div>

						{/* Last Name Filter */}
						<div style={{ marginBottom: '12px' }}>
							<Typography.Text strong style={{ fontSize: '12px' }}>
								Last Name:
							</Typography.Text>
							<div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
								<Select
									value={lastNameOperator}
									onChange={setLastNameOperator}
									options={textOperatorOptions}
									style={{ width: '120px' }}
									size="small"
									getPopupContainer={(trigger) => trigger.parentNode}
								/>
								<Input
									placeholder="Value"
									value={lastNameValue}
									onChange={(e) => setLastNameValue(e.target.value)}
									size="small"
									style={{ flex: 1 }}
								/>
							</div>
							<Checkbox
								checked={lastNameCaseSensitive}
								onChange={(e) => setLastNameCaseSensitive(e.target.checked)}
								style={{ marginTop: '4px', fontSize: '12px' }}
							>
								Case sensitive
							</Checkbox>
						</div>

						{/* Job Title Filter */}
						<div style={{ marginBottom: '12px' }}>
							<Typography.Text strong style={{ fontSize: '12px' }}>
								Job Title:
							</Typography.Text>
							<div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
								<Select
									value={titleOperator}
									onChange={setTitleOperator}
									options={textOperatorOptions}
									style={{ width: '120px' }}
									size="small"
									getPopupContainer={(trigger) => trigger.parentNode}
								/>
								<Input
									placeholder="Value"
									value={titleValue}
									onChange={(e) => setTitleValue(e.target.value)}
									size="small"
									style={{ flex: 1 }}
								/>
							</div>
							<Checkbox
								checked={titleCaseSensitive}
								onChange={(e) => setTitleCaseSensitive(e.target.checked)}
								style={{ marginTop: '4px', fontSize: '12px' }}
							>
								Case sensitive
							</Checkbox>
						</div>
					</div>
				) : dataType === 'text' ? (
					<div>
						<div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
							<Select
								value={textOperator}
								onChange={setTextOperator}
								options={textOperatorOptions}
								style={{ width: '120px' }}
								size="small"
								getPopupContainer={(trigger) => trigger.parentNode}
							/>
							<Input
								placeholder={`Filter ${field}`}
								value={textValue}
								onChange={(e) => setTextValue(e.target.value)}
								onPressEnter={handleApplyFilter}
								size="small"
								style={{ flex: 1 }}
							/>
						</div>
						{field !== 'employeeId' && (
							<Checkbox
								checked={caseSensitive}
								onChange={(e) => setCaseSensitive(e.target.checked)}
								style={{ marginBottom: '8px', fontSize: '12px' }}
							>
								Case sensitive
							</Checkbox>
						)}
					</div>
				) : dataType === 'multiSelect' ? (
					<Select
						mode="multiple"
						placeholder={`Select ${field}`}
						value={selectedValues}
						onChange={setSelectedValues}
						style={{ width: '100%', marginBottom: '8px' }}
						options={options}
						getPopupContainer={(trigger) => trigger.parentNode}
					/>
				) : dataType === 'boolean' ? (
					<Select
						placeholder="Select status"
						value={booleanValue}
						onChange={setBooleanValue}
						style={{ width: '100%', marginBottom: '8px' }}
						options={[
							{ label: 'Active', value: 'active' },
							{ label: 'Inactive', value: 'inactive' },
						]}
						getPopupContainer={(trigger) => trigger.parentNode}
					/>
				) : dataType === 'date' ? (
					<div>
						<Select
							value={dateOperator}
							onChange={setDateOperator}
							options={dateOperatorOptions}
							style={{ width: '100%', marginBottom: '8px' }}
							getPopupContainer={(trigger) => trigger.parentNode}
						/>
						{dateOperator === 'between' ? (
							<RangePicker
								value={startDate && endDate ? [startDate, endDate] : []}
								onChange={(dates) => {
									if (dates && dates.length === 2) {
										setStartDate(dates[0]);
										setEndDate(dates[1]);
									} else {
										setStartDate(null);
										setEndDate(null);
									}
								}}
								style={{ width: '100%', marginBottom: '8px' }}
								getPopupContainer={(trigger) => trigger.parentNode}
							/>
						) : (
							<DatePicker
								value={singleDate}
								onChange={setSingleDate}
								style={{ width: '100%', marginBottom: '8px' }}
								getPopupContainer={(trigger) => trigger.parentNode}
							/>
						)}
					</div>
				) : null}

				<div
					style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
				>
					<Button size="small" type="primary" onClick={handleApplyFilter}>
						Apply
					</Button>
					<Button size="small" onClick={handleClearFilter}>
						Clear
					</Button>
					<Button size="small" onClick={() => setActiveFilterDropdown(null)}>
						Cancel
					</Button>
				</div>
			</div>
		);
	});

	// Filter icon click handler
	const handleFilterClick = useCallback(
		(e, field) => {
			e.stopPropagation();
			setActiveFilterDropdown(activeFilterDropdown === field ? null : field);
		},
		[activeFilterDropdown]
	);

	// Close filter dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (activeFilterDropdown) {
				// Check if click is inside filter dropdown container or its dropdowns
				const isInsideFilterContainer = e.target.closest(
					'.filter-dropdown-container'
				);
				const isInsideSelectDropdown = e.target.closest('.ant-select-dropdown');
				const isInsideDatePicker = e.target.closest('.ant-picker-dropdown');

				if (
					!isInsideFilterContainer &&
					!isInsideSelectDropdown &&
					!isInsideDatePicker
				) {
					setActiveFilterDropdown(null);
				}
			}
		};

		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, [activeFilterDropdown]);

	// Column resize handlers
	const handleMouseDown = useCallback(
		(e, columnKey) => {
			e.preventDefault();
			e.stopPropagation(); // Prevent triggering sort
			setIsResizing(true);
			setResizeColumnKey(columnKey);
			setStartX(e.clientX);
			setStartWidth(columnWidths[columnKey]);
		},
		[columnWidths]
	);

	const handleMouseMove = useCallback(
		(e) => {
			if (!isResizing || !resizeColumnKey) return;

			const diff = e.clientX - startX;
			const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px

			setColumnWidths((prev) => ({
				...prev,
				[resizeColumnKey]: newWidth,
			}));
		},
		[isResizing, resizeColumnKey, startX, startWidth]
	);

	const handleMouseUp = useCallback(() => {
		setIsResizing(false);
		setResizeColumnKey(null);
		setStartX(0);
		setStartWidth(0);
	}, []);

	// global mouse event listeners for resizing
	useEffect(() => {
		if (isResizing) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			document.body.style.cursor = 'col-resize';
			document.body.style.userSelect = 'none';

			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
				document.body.style.cursor = 'default';
				document.body.style.userSelect = 'auto';
			};
		}
	}, [isResizing, handleMouseMove, handleMouseUp]);

	const loadMoreData = useCallback(() => {
		if (hasMoreData && !loading && nextTokenWithPage?.nextToken) {
			fetchData({
				page: currentPage + 1,
				resetData: false,
				nextTokenArg: nextTokenWithPage?.nextToken,
			});
			setCurrentPage(currentPage + 1);
		}
	}, [
		fetchData,
		currentPage,
		hasMoreData,
		loading,
		nextTokenWithPage,
		data.length,
		totalRecords,
	]);

	useEffect(() => {
		const fetchUserGroupsAndDepartments = async () => {
			try {
				const { data: userGroupsData } = await client.query({
					query: QUERY_USERGROUPS,
					variables: { companyId, first: 100 },
					fetchPolicy: 'network-only',
				});

				if (userGroupsData?.queryUserGroupsByCompanyIdIndex?.items) {
					setUserGroups(userGroupsData.queryUserGroupsByCompanyIdIndex.items);
				}

				const { data: departmentsData } = await client.query({
					query: QUERY_DEPARTMENTS,
					variables: { companyId, first: 100 },
					fetchPolicy: 'network-only',
				});

				if (departmentsData?.queryDepartmentsByCompanyIdIndex?.items) {
					setDepartments(
						departmentsData.queryDepartmentsByCompanyIdIndex.items
					);
				}
			} catch (error) {
				console.error('Error fetching usergroups or departments:', error);
			}
		};

		fetchUserGroupsAndDepartments();
	}, [client, companyId]);

	// Initial data fetch
	useEffect(() => {
		fetchData({ page: 0, resetData: true });
	}, [fetchData]);

	// reset pagination when filters/sort change
	useEffect(() => {
		setCurrentPage(0);
		setNextTokenWithPage({ nextToken: null, page: 0 });
	}, [filterModel, sortModel]);

	const handleOpenModal = useCallback((employeeId) => {
		setSelectedEmployeeId(employeeId);
		setModalVisible(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalVisible(false);
		setSelectedEmployeeId(null);
	}, []);

	const handleFilter = useCallback((fieldOrFilterModel, value) => {
		if (typeof fieldOrFilterModel === 'string') {
			const fieldName = fieldOrFilterModel;
			setFilterModel((prevFilter) => {
				if (value === null || value === '') {
					const newFilter = { ...prevFilter };
					delete newFilter[fieldName];
					return Object.keys(newFilter).length > 0 ? newFilter : null;
				} else {
					return {
						...prevFilter,
						[fieldName]: value,
					};
				}
			});
		} else {
			setFilterModel(fieldOrFilterModel);
		}
	}, []);

	const toggleFilterDropdown = useCallback((columnKey) => {
		setFilterDropdowns((prev) => ({
			...prev,
			[columnKey]: !prev[columnKey],
		}));
	}, []);

	const closeFilterDropdown = useCallback((columnKey) => {
		setFilterDropdowns((prev) => ({
			...prev,
			[columnKey]: false,
		}));
	}, []);

	const onExportClick = useCallback(async () => {
		try {
			setExportLoading(true);
			setPercentageLoaded(0);

			const escapeCSVField = (field) => {
				if (field == null) return '';
				const str = String(field);
				return `"${str.replace(/"/g, '""')}"`;
			};

			const exportPageSize = 100;
			const BATCH_SIZE = 30;
			let allRowsMap = new Map();

			const generateOffsetToken = (offset) => {
				return btoa(JSON.stringify({ offset }));
			};

			const initialQueryVariables = {
				companyId,
				limit: 5,
			};

			if (sortModel) {
				initialQueryVariables.sort = sortModel;
			}

			if (filterModel) {
				initialQueryVariables.filter = filterModel;
			}

			const initialResponse = await client.query({
				query: QUERY_EMPLOYEES,
				variables: initialQueryVariables,
				fetchPolicy: 'network-only',
			});

			const totalRecordsToExport =
				initialResponse.data.queryUsersByCompanyIdPostgres.totalCount;
			setTotalRecords(totalRecordsToExport);

			if (totalRecordsToExport === 0) {
				setExportLoading(false);
				return;
			}

			const totalPages = Math.ceil(totalRecordsToExport / exportPageSize);

			for (let startPage = 0; startPage < totalPages; startPage += BATCH_SIZE) {
				const batchPromises = [];
				const batchPageIndices = [];

				const endPage = Math.min(startPage + BATCH_SIZE, totalPages);

				for (let page = startPage; page < endPage; page++) {
					const offset = page * exportPageSize;

					const queryVariables = {
						companyId,
						limit: exportPageSize,
						nextToken: generateOffsetToken(offset),
					};

					if (sortModel) {
						queryVariables.sort = sortModel;
					}

					if (filterModel) {
						queryVariables.filter = filterModel;
					}

					batchPromises.push(
						client.query({
							query: QUERY_EMPLOYEES,
							variables: queryVariables,
							fetchPolicy: 'network-only',
						})
					);

					batchPageIndices.push(page);
				}

				const batchResults = await Promise.all(batchPromises);

				for (let i = 0; i < batchResults.length; i++) {
					const pageIndex = batchPageIndices[i];
					const response = batchResults[i];

					const items = response.data.queryUsersByCompanyIdPostgres.items;

					const rows = items.map((item) => ({
						...item,
						location: item.additionalInfo?.location || '',
						budget:
							'$' +
							(item.additionalInfo?.recognitionEvents?.currentBudget ||
								currentUser?.company?.recognitionSettings?.baseSnappyBudget ||
								'0'),
					}));

					allRowsMap.set(pageIndex, rows);
				}

				const processedPages = Math.min(endPage, totalPages);
				const processedRecords = Math.min(
					processedPages * exportPageSize,
					totalRecordsToExport
				);
				const progress = Math.min(
					Math.round((processedRecords / totalRecordsToExport) * 100),
					99
				);

				setPercentageLoaded(progress);

				await new Promise((resolve) => setTimeout(resolve, 30));
			}

			const allRows = Array.from(allRowsMap)
				.sort(([pageA], [pageB]) => pageA - pageB)
				.flatMap(([_, rows]) => rows);

			const headers = [
				'First Name',
				'Last Name',
				'Email',
				'Role',
				'User Group',
				'Department',
				'Job Title',
				'Status',
				'Date Created',
				'Location',
				'Employee ID',
				'Budget',
			].join(',');

			const csvContent = allRows
				.map((row) => {
					const userGroupId = row.userGroupId;
					const userGroup = userGroups.find(
						(group) => group.id === userGroupId
					);
					const userGroupName = userGroup ? userGroup.name : 'Not Assigned';

					const departmentId = row.departmentId;
					const department = departments.find(
						(dept) => dept.id === departmentId
					);
					const departmentName = department ? department.name : 'Not Assigned';

					const locationValue = row.additionalInfo?.location
						? typeof row.additionalInfo.location === 'object'
							? JSON.stringify(row.additionalInfo.location)
							: row.additionalInfo.location
						: '';

					return [
						`"${row.firstName || ''}"`,
						`"${row.lastName || ''}"`,
						`"${row.emailAddress || ''}"`,
						`"${row.role || ''}"`,
						`"${userGroupName}"`,
						`"${departmentName}"`,
						`"${row.title || ''}"`,
						`"${row.active ? 'Active' : 'Inactive'}"`,
						`"${row.dateCreated ? new Date(row.dateCreated).toLocaleDateString() : ''}"`,
						escapeCSVField(locationValue),
						`"${row.employeeId || ''}"`,
						`"${row.additionalInfo?.recognitionEvents?.currentBudget ? '$' + row.additionalInfo?.recognitionEvents?.currentBudget : '$0'}"`,
					].join(',');
				})
				.join('\n');

			const finalCsv = `${headers}\n${csvContent}`;

			setPercentageLoaded(100);

			const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute(
				'download',
				`employees_export_${filterModel ? 'filtered' : 'full'}_${new Date().toISOString().slice(0, 10)}.csv`
			);
			document.body.appendChild(link);

			await new Promise((resolve) => setTimeout(resolve, 300));

			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error('CSV export failed:', error);
			alert('Export failed. Please try again.');
		} finally {
			setExportLoading(false);
		}
	}, [
		client,
		companyId,
		filterModel,
		sortModel,
		currentUser,
		userGroups,
		departments,
	]);

	const VirtualizedRow = React.memo(({ index, style, data: rowData }) => {
		const record = rowData[index];
		if (!record) return null;

		if (index >= rowData.length - 20) {
			loadMoreData();
		}

		// Memoize expensive computations
		const userGroup = React.useMemo(
			() => userGroups.find((group) => group.id === record.userGroupId),
			[record.userGroupId]
		);

		const department = React.useMemo(
			() => departments.find((dept) => dept.id === record.departmentId),
			[record.departmentId]
		);

		const employeeBudget = React.useMemo(() => {
			const recognitionEvents = record?.additionalInfo?.recognitionEvents;
			let budget =
				recognitionEvents?.currentBudget ??
				currentUser?.company?.recognitionSettings?.baseSnappyBudget ??
				0;
			if (recognitionEvents?.additionalBudgetTotal > 0) {
				budget += recognitionEvents?.additionalBudgetTotal;
			}
			if (recognitionEvents?.amountPending > 0) {
				budget -= recognitionEvents?.amountPending;
			}
			return budget || '0';
		}, [
			record?.additionalInfo?.recognitionEvents,
			currentUser?.company?.recognitionSettings?.baseSnappyBudget,
		]);

		return (
			<div
				style={{
					...style,
					display: 'flex',
					alignItems: 'center',
					borderBottom: '1px solid #f0f0f0',
					backgroundColor: '#ffffff',
					transition: 'background-color 0.15s ease',
					minWidth: '1200px',
				}}
				onMouseEnter={(e) =>
					(e.currentTarget.style.backgroundColor = '#fafafa')
				}
				onMouseLeave={(e) =>
					(e.currentTarget.style.backgroundColor = '#ffffff')
				}
			>
				{/* Actions Column */}
				<div
					style={{
						width: columnWidths.actions,
						padding: '12px 16px 12px 16px',
						flexShrink: 0,
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<EditOutlined
						onClick={() => handleOpenModal(record.id)}
						style={{
							fontSize: '16px',
							color: '#1890ff',
							cursor: 'pointer',
							transition: 'color 0.3s ease',
						}}
						onMouseEnter={(e) => (e.target.style.color = '#40a9ff')}
						onMouseLeave={(e) => (e.target.style.color = '#1890ff')}
					/>
				</div>
				{/* Employee Column */}
				<div
					style={{
						width: columnWidths.employee,
						padding: '12px 12px 12px 16px',
						display: 'flex',
						alignItems: 'center',
						gap: 12,
						flexShrink: 0,
					}}
				>
					<div
						style={{
							flexShrink: 0,
							cursor: adminPermissions ? 'pointer' : 'default',
							position: 'relative',
						}}
						onDoubleClick={() =>
							adminPermissions &&
							handleCopyUserId(
								record.id,
								`${record.firstName} ${record.lastName}`
							)
						}
						title={
							adminPermissions
								? 'Double-click to copy user ID (Admin only)'
								: undefined
						}
					>
						{!record?.avatar || record?.avatar === '' ? (
							<div
								style={{
									width: 40,
									height: 40,
									borderRadius: '50%',
									backgroundColor: nameToColor(
										record.firstName + ' ' + record.lastName
									),
									color: '#fff',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '14px',
									fontWeight: 600,
									border: '2px solid #f0f0f0',
									transition: adminPermissions ? 'transform 0.1s ease' : 'none',
								}}
								onMouseDown={
									adminPermissions
										? (e) => (e.currentTarget.style.transform = 'scale(0.95)')
										: undefined
								}
								onMouseUp={
									adminPermissions
										? (e) => (e.currentTarget.style.transform = 'scale(1)')
										: undefined
								}
								onMouseLeave={
									adminPermissions
										? (e) => (e.currentTarget.style.transform = 'scale(1)')
										: undefined
								}
							>
								{record?.firstName && record.firstName[0]?.toUpperCase()}
								{record?.lastName && record.lastName[0]?.toUpperCase()}
							</div>
						) : (
							<Avatar
								size={40}
								src={record?.avatar || null}
								style={{
									backgroundColor: nameToColor(
										record.firstName + ' ' + record.lastName
									),
									color: '#fff',
									border: '2px solid #f0f0f0',
									transition: adminPermissions ? 'transform 0.1s ease' : 'none',
								}}
								alt={`Avatar of ${record?.firstName} ${record?.lastName}`}
								onMouseDown={
									adminPermissions
										? (e) => (e.currentTarget.style.transform = 'scale(0.95)')
										: undefined
								}
								onMouseUp={
									adminPermissions
										? (e) => (e.currentTarget.style.transform = 'scale(1)')
										: undefined
								}
								onMouseLeave={
									adminPermissions
										? (e) => (e.currentTarget.style.transform = 'scale(1)')
										: undefined
								}
							/>
						)}
					</div>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							minWidth: 0,
							flex: 1,
						}}
					>
						<div
							style={{
								fontWeight: 800,
								fontSize: '14px',
								lineHeight: '20px',
								color: '#rgba(0, 0, 0, 0.65)',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{record.firstName} {record.lastName}
						</div>
						<div
							style={{
								fontSize: '11px',
								lineHeight: '18px',
								color: '#rgba(0, 0, 0, 0.45)',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{record.title || 'No job title'}
						</div>
					</div>
				</div>

				{/* Email Column */}
				<div
					style={{
						width: columnWidths.email,
						padding: '12px',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						color: 'rgba(0, 0, 0, 0.65)',
						flexShrink: 0,
					}}
				>
					{record.emailAddress}
				</div>
				{/* Budget Column */}
				<div
					style={{
						width: columnWidths.budget,
						padding: '12px',
						fontWeight: 900,
						color: 'oklch(0.5307 0.1791 144.1)',
						flexShrink: 0,
					}}
				>
					${employeeBudget}
				</div>
				{/* Role Column */}
				<div
					style={{ width: columnWidths.role, padding: '12px', flexShrink: 0 }}
				>
					<Tag
						style={{
							borderRadius: '4px',
							color: ROLE_COLORS?.[record.role]?.normal,
							borderColor: ROLE_COLORS[record.role]?.lighter,
							background: ROLE_COLORS[record.role]?.lightest,
							padding: '0 7px',
							fontWeight: 500,
							fontSize: '12px',
							margin: 0,
							fontVariant: 'tabular-nums',
							fontFeatureSettings: 'tnum',
							boxSizing: 'border-box',
							transition: 'all 0.3s',
							lineHeight: '20px',
						}}
						color={ROLE_COLORS[record.role] || '#2db7f5'}
					>
						{roleMap?.[record.role]}
					</Tag>
				</div>

				{/* User Group Column */}
				{/* <div style={{ width: columnWidths.userGroup, padding: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(0, 0, 0, 0.65)' }}>
                    {userGroup ? userGroup.name : 'Not Assigned'}
                </div> */}

				{/* Department Column */}
				<div
					style={{
						width: columnWidths.department,
						padding: '12px',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						color: 'rgba(0, 0, 0, 0.65)',
						flexShrink: 0,
					}}
				>
					{department ? department.name : 'Not Assigned'}
				</div>
				{/* Employee ID Column */}
				<div
					style={{
						width: columnWidths.employeeId,
						padding: '12px',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						color: 'rgba(0, 0, 0, 0.65)',
						flexShrink: 0,
					}}
				>
					{record.employeeId || '-'}
				</div>
				{/* Date Created Column */}
				<div
					style={{
						width: columnWidths.dateCreated,
						padding: '12px',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						color: 'rgba(0, 0, 0, 0.65)',
						flexShrink: 0,
					}}
				>
					{dayjs(record.dateCreated).format('MM/DD/YYYY')}
				</div>
				{/* Status Column */}
				<div
					style={{ width: columnWidths.status, padding: '12px', flexShrink: 0 }}
				>
					<Tag color={record.active ? 'green' : 'red'}>
						{record.active ? 'Active' : 'Inactive'}
					</Tag>
				</div>
			</div>
		);
	});

	// Resize Handle Component
	const ResizeHandle = React.memo(({ columnKey }) => (
		<div
			style={{
				position: 'absolute',
				right: 0,
				top: 0,
				bottom: 0,
				width: 4,
				cursor: 'col-resize',
				backgroundColor: 'transparent',
				borderRight: '1px solid #f0f0f0',
				zIndex: 1,
			}}
			onMouseDown={(e) => handleMouseDown(e, columnKey)}
			onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1890ff')}
			onMouseLeave={(e) =>
				(e.currentTarget.style.backgroundColor = 'transparent')
			}
		/>
	));

	// Virtual Table Header
	const VirtualTableHeader = React.memo(() => (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				height: 64,
				backgroundColor: '#f8f8f8',
				borderBottom: '1px solid rgb(240, 240, 240)',
				fontWeight: 600,
				fontSize: '14px',
				color: 'rgb(38, 38, 38)',
				position: 'sticky',
				top: '0px',
				zIndex: 10,
				minWidth: '1200px',
			}}
		>
			<div
				style={{
					width: columnWidths.actions,
					padding: '12px 16px 12px 16px',
					position: 'relative',
					fontWeight: 900,
					flexShrink: 0,
					textAlign: 'center',
				}}
			>
				View
				<ResizeHandle columnKey="actions" />
			</div>
			<div
				style={{
					width: columnWidths.employee,
					padding: '12px 12px 12px 16px',
					display: 'flex',
					alignItems: 'flex-start',
					gap: 8,
					position: 'relative',
					flexShrink: 0,
				}}
				className="filter-dropdown-container"
			>
				<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
					<span
						style={{
							cursor: 'pointer',
							flex: 1,
							lineHeight: '20px',
							paddingLeft: '52px',
							fontWeight: 900,
						}}
						onClick={() => handleSort('firstName')}
					>
						Employee
					</span>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							flexShrink: 0,
							marginTop: '1px',
						}}
					>
						<SortIcon field="firstName" />
						<FilterOutlined
							style={{
								color:
									filterModel?.firstName ||
									filterModel?.lastName ||
									filterModel?.title
										? '#1890ff'
										: 'rgba(0, 0, 0, 0.45)',
								fontSize: '12px',
								cursor: 'pointer',
							}}
							onClick={(e) => handleFilterClick(e, 'employee')}
						/>
					</div>
				</div>
				<FilterDropdown field="employee" dataType="text" />
				<ResizeHandle columnKey="employee" />
			</div>
			<div
				style={{
					width: columnWidths.email,
					padding: '12px',
					display: 'flex',
					alignItems: 'flex-start',
					gap: 8,
					position: 'relative',
					fontWeight: 900,
					flexShrink: 0,
				}}
				className="filter-dropdown-container"
			>
				<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
					<span
						style={{ cursor: 'pointer', flex: 1, lineHeight: '20px' }}
						onClick={() => handleSort('emailAddress')}
					>
						Email
					</span>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							flexShrink: 0,
							marginTop: '1px',
						}}
					>
						<SortIcon field="emailAddress" />
						<FilterOutlined
							style={{
								color: filterModel?.emailAddress
									? '#1890ff'
									: 'rgba(0, 0, 0, 0.45)',
								fontSize: '12px',
								cursor: 'pointer',
							}}
							onClick={(e) => handleFilterClick(e, 'emailAddress')}
						/>
					</div>
				</div>
				<FilterDropdown field="emailAddress" dataType="text" />
				<ResizeHandle columnKey="email" />
			</div>
			<div
				style={{
					width: columnWidths.budget,
					padding: '12px',
					position: 'relative',
					fontWeight: 900,
					flexShrink: 0,
				}}
			>
				Budget
				<ResizeHandle columnKey="budget" />
			</div>
			<div
				style={{
					width: columnWidths.role,
					padding: '12px',
					display: 'flex',
					alignItems: 'flex-start',
					gap: 8,
					position: 'relative',
					fontWeight: 900,
					flexShrink: 0,
				}}
				className="filter-dropdown-container"
			>
				<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
					<span
						style={{ cursor: 'pointer', flex: 1, lineHeight: '20px' }}
						onClick={() => handleSort('role')}
					>
						Role
					</span>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							flexShrink: 0,
							marginTop: '1px',
						}}
					>
						<SortIcon field="role" />
						<FilterOutlined
							style={{
								color: filterModel?.role ? '#1890ff' : 'rgba(0, 0, 0, 0.45)',
								fontSize: '12px',
								cursor: 'pointer',
							}}
							onClick={(e) => handleFilterClick(e, 'role')}
						/>
					</div>
				</div>
				<FilterDropdown
					field="role"
					dataType="multiSelect"
					options={[
						{ label: 'Admin', value: 'admin' },
						{ label: 'Manager', value: 'manager' },
						{ label: 'Employee', value: 'employee' },
						{ label: 'Extended User', value: 'extendedUser' },
						{ label: 'Super Admin', value: 'superAdmin' },
						{ label: 'Support Admin', value: 'supportAdmin' },
					]}
				/>
				<ResizeHandle columnKey="role" />
			</div>
			{/* <div
                style={{ width: columnWidths.userGroup, padding: '12px', display: 'flex', alignItems: 'flex-start', gap: 8, position: 'relative', fontWeight: 900 }}
                className="filter-dropdown-container"
            >
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span
                        style={{ cursor: 'pointer', flex: 1, lineHeight: '20px' }}
                        onClick={() => handleSort('userGroupId')}
                    >
                        User Group
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, marginTop: '1px' }}>
                        <SortIcon field="userGroupId" />
                        <FilterOutlined
                            style={{
                                color: filterModel?.userGroupId ? '#1890ff' : 'rgba(0, 0, 0, 0.45)',
                                fontSize: '12px',
                                cursor: 'pointer'
                            }}
                            onClick={(e) => handleFilterClick(e, 'userGroupId')}
                        />
                    </div>
                </div>
                <FilterDropdown
                    field="userGroupId"
                    dataType="multiSelect"
                    options={userGroups.map(group => ({ label: group.name, value: group.id }))}
                />
                <ResizeHandle columnKey="userGroup" />
            </div> */}
			<div
				style={{
					width: columnWidths.department,
					padding: '12px',
					display: 'flex',
					alignItems: 'flex-start',
					gap: 8,
					position: 'relative',
					fontWeight: 900,
					flexShrink: 0,
				}}
				className="filter-dropdown-container"
			>
				<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
					<span
						style={{ cursor: 'pointer', flex: 1, lineHeight: '20px' }}
						onClick={() => handleSort('departmentId')}
					>
						Department
					</span>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							flexShrink: 0,
							marginTop: '1px',
						}}
					>
						<SortIcon field="departmentId" />
						<FilterOutlined
							style={{
								color: filterModel?.departmentId
									? '#1890ff'
									: 'rgba(0, 0, 0, 0.45)',
								fontSize: '12px',
								cursor: 'pointer',
							}}
							onClick={(e) => handleFilterClick(e, 'departmentId')}
						/>
					</div>
				</div>
				<FilterDropdown
					field="departmentId"
					dataType="multiSelect"
					options={departments.map((dept) => ({
						label: dept.name,
						value: dept.id,
					}))}
				/>
				<ResizeHandle columnKey="department" />
			</div>
			<div
				style={{
					width: columnWidths.employeeId,
					padding: '12px',
					display: 'flex',
					alignItems: 'flex-start',
					gap: 8,
					position: 'relative',
					fontWeight: 900,
					flexShrink: 0,
				}}
				className="filter-dropdown-container"
			>
				<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
					<span
						style={{ cursor: 'pointer', flex: 1, lineHeight: '20px' }}
						onClick={() => handleSort('employeeId')}
					>
						Employee ID
					</span>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							flexShrink: 0,
							marginTop: '1px',
						}}
					>
						<SortIcon field="employeeId" />
						<FilterOutlined
							style={{
								color: filterModel?.employeeId
									? '#1890ff'
									: 'rgba(0, 0, 0, 0.45)',
								fontSize: '12px',
								cursor: 'pointer',
							}}
							onClick={(e) => handleFilterClick(e, 'employeeId')}
						/>
					</div>
				</div>
				<FilterDropdown field="employeeId" dataType="text" />
				<ResizeHandle columnKey="employeeId" />
			</div>
			<div
				style={{
					width: columnWidths.dateCreated,
					padding: '12px',
					display: 'flex',
					alignItems: 'flex-start',
					gap: 4,
					position: 'relative',
					fontWeight: 900,
					flexShrink: 0,
				}}
				className="filter-dropdown-container"
			>
				<div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
					<span
						style={{ cursor: 'pointer', flex: 1, lineHeight: '20px' }}
						onClick={() => handleSort('dateCreated')}
					>
						Date Created
					</span>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							flexShrink: 0,
							marginTop: '1px',
						}}
					>
						<SortIcon field="dateCreated" />
						<FilterOutlined
							style={{
								color: filterModel?.dateCreated
									? '#1890ff'
									: 'rgba(0, 0, 0, 0.45)',
								fontSize: '12px',
								cursor: 'pointer',
							}}
							onClick={(e) => handleFilterClick(e, 'dateCreated')}
						/>
					</div>
				</div>
				<FilterDropdown field="dateCreated" dataType="date" />
				<ResizeHandle columnKey="dateCreated" />
			</div>
			<div
				style={{
					width: columnWidths.status,
					padding: '12px',
					display: 'flex',
					alignItems: 'flex-start',
					gap: 8,
					position: 'relative',
					fontWeight: 900,
					flexShrink: 0,
				}}
				className="filter-dropdown-container"
			>
				<div
					style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 4 }}
				>
					<span
						style={{ cursor: 'pointer', flex: 1, lineHeight: '20px' }}
						onClick={() => handleSort('active')}
					>
						Status
					</span>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							flexShrink: 0,
							marginTop: '1px',
						}}
					>
						<SortIcon field="active" />
						<FilterOutlined
							style={{
								color: filterModel?.active ? '#1890ff' : 'rgba(0, 0, 0, 0.45)',
								fontSize: '12px',
								cursor: 'pointer',
							}}
							onClick={(e) => handleFilterClick(e, 'active')}
						/>
					</div>
				</div>
				<FilterDropdown field="active" dataType="boolean" />
				<ResizeHandle columnKey="status" />
			</div>
		</div>
	));

	// Column definitions (keeping for potential fallback or export functionality)
	const columns = useMemo(
		() => [
			{
				title: 'View',
				dataIndex: 'actions',
				key: 'actions',
				width: 80,
				align: 'center',
				render: (_, record) => (
					<EditOutlined
						onClick={() => handleOpenModal(record.id)}
						style={{
							fontSize: '16px',
							color: '#1890ff',
							cursor: 'pointer',
							transition: 'color 0.3s ease',
						}}
						onMouseEnter={(e) => (e.target.style.color = '#40a9ff')}
						onMouseLeave={(e) => (e.target.style.color = '#1890ff')}
					/>
				),
			},
			{
				title: 'Employee',
				dataIndex: 'employee',
				key: 'employee',
				width: 280,
				sortDirections: ['ascend', 'descend'],
				sorter: (a, b) => {
					const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
					const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
					return nameA.localeCompare(nameB);
				},
				filterDropdown: (props) => (
					<div style={{ padding: 16, minWidth: 280 }}>
						<div style={{ marginBottom: 12 }}>
							<Typography.Text
								strong
								style={{ display: 'block', marginBottom: 4 }}
							>
								First Name:
							</Typography.Text>
							<Input
								placeholder="Filter by first name"
								value={filterModel?.firstName || ''}
								onChange={(e) => handleFilter('firstName', e.target.value)}
								style={{ width: '100%' }}
							/>
						</div>
						<div style={{ marginBottom: 12 }}>
							<Typography.Text
								strong
								style={{ display: 'block', marginBottom: 4 }}
							>
								Last Name:
							</Typography.Text>
							<Input
								placeholder="Filter by last name"
								value={filterModel?.lastName || ''}
								onChange={(e) => handleFilter('lastName', e.target.value)}
								style={{ width: '100%' }}
							/>
						</div>
						<div>
							<Typography.Text
								strong
								style={{ display: 'block', marginBottom: 4 }}
							>
								Job Title:
							</Typography.Text>
							<Input
								placeholder="Filter by job title"
								value={filterModel?.title || ''}
								onChange={(e) => handleFilter('title', e.target.value)}
								style={{ width: '100%' }}
							/>
						</div>
					</div>
				),
				filterIcon: (filtered) => (
					<FilterOutlined
						style={{
							color:
								filterModel?.firstName ||
								filterModel?.lastName ||
								filterModel?.title
									? '#1890ff'
									: undefined,
						}}
					/>
				),
				render: (_, record) => (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 12,
							padding: '4px 0',
						}}
					>
						<div style={{ flexShrink: 0 }}>
							{!record?.avatar || record?.avatar === '' ? (
								<div
									style={{
										width: 40,
										height: 40,
										borderRadius: '50%',
										backgroundColor: nameToColor(
											record.firstName + ' ' + record.lastName
										),
										color: '#fff',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: '14px',
										fontWeight: 600,
										border: '2px solid #f0f0f0',
									}}
								>
									{record?.firstName && record.firstName[0]?.toUpperCase()}
									{record?.lastName && record.lastName[0]?.toUpperCase()}
								</div>
							) : (
								<Avatar
									size={40}
									src={record?.avatar || null}
									style={{
										backgroundColor: nameToColor(
											record.firstName + ' ' + record.lastName
										),
										color: '#fff',
										border: '2px solid #f0f0f0',
									}}
									alt={`Avatar of ${record?.firstName} ${record?.lastName}`}
								/>
							)}
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								minWidth: 0,
								flex: 1,
							}}
						>
							<div
								style={{
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '20px',
									color: '#262626',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
							>
								{record.firstName} {record.lastName}
							</div>
							<div
								style={{
									fontSize: '12px',
									lineHeight: '18px',
									color: '#595959',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
							>
								{record.title || 'No job title'}
							</div>
						</div>
					</div>
				),
			},
			{
				title: 'Email',
				dataIndex: 'emailAddress',
				key: 'emailAddress',
				width: 150,
				sortDirections: ['ascend', 'descend'],
				sorter: true,
				dataType: 'text',
				ellipsis: {
					showTitle: false,
				},
				render: (emailAddress) => (
					<Tooltip placement="topLeft" title={emailAddress}>
						{emailAddress}
					</Tooltip>
				),
				filterDropdown: ({
					setSelectedKeys,
					selectedKeys,
					confirm,
					clearFilters,
				}) => (
					<CustomFilterDropdown
						column={{
							dataIndex: 'emailAddress',
							title: 'Email',
							dataType: 'text',
						}}
						filterModel={filterModel}
						onFilter={handleFilter}
						onClose={() => closeFilterDropdown('emailAddress')}
						visible={filterDropdowns.emailAddress}
						setFilterDropdown={setFilterDropdowns}
					/>
				),
				filterIcon: (filtered) => (
					<FilterOutlined
						style={{ color: filtered ? '#1890ff' : undefined }}
						onClick={() => toggleFilterDropdown('emailAddress')}
					/>
				),
			},
			{
				title: 'Role',
				dataIndex: 'role',
				key: 'role',
				width: 50,
				sortDirections: ['ascend', 'descend'],
				sorter: true,
				dataType: 'multiSelect',
				render: (role) => (
					<Tag
						style={{
							borderRadius: '4px',
							color: ROLE_COLORS?.[role]?.normal,
							borderColor: ROLE_COLORS[role]?.lighter,
							background: ROLE_COLORS[role]?.lightest,
							padding: '0 7px',
							fontWeight: 500,
							//textShadow: '1px 1px 0px #00000026',
							fontSize: '12px',
							margin: 0,
							fontVariant: 'tabular-nums',
							fontFeatureSettings: 'tnum',
							boxSizing: 'border-box',
							transition: 'all 0.3s',
							lineHeight: '20px',
						}}
						color={ROLE_COLORS[role] || '#2db7f5'}
					>
						{roleMap?.[role]}
					</Tag>
				),
				filterDropdown: ({
					setSelectedKeys,
					selectedKeys,
					confirm,
					clearFilters,
				}) => (
					<CustomFilterDropdown
						column={{
							dataIndex: 'role',
							title: 'Role',
							dataType: 'multiSelect',
						}}
						filterModel={filterModel}
						onFilter={handleFilter}
						onClose={() => closeFilterDropdown('role')}
						visible={filterDropdowns.role}
						multiSelectOptions={roleOptions}
						setFilterDropdown={setFilterDropdowns}
					/>
				),
				filterIcon: (filtered) => (
					<FilterOutlined
						style={{ color: filtered ? '#1890ff' : undefined }}
						onClick={() => toggleFilterDropdown('role')}
					/>
				),
			},
			{
				title: 'User Group',
				dataIndex: 'userGroupId',
				key: 'userGroup',
				width: 100,
				dataType: 'multiSelect',
				ellipsis: {
					showTitle: false,
				},
				render: (userGroupId) => {
					const userGroup = userGroups.find(
						(group) => group.id === userGroupId
					);
					return (
						<Tooltip
							placement="topLeft"
							title={userGroup ? userGroup.name : userGroupId}
						>
							{userGroup ? userGroup.name : 'Not Assigned'}
						</Tooltip>
					);
				},
				filterDropdown: ({
					setSelectedKeys,
					selectedKeys,
					confirm,
					clearFilters,
				}) => (
					<CustomFilterDropdown
						column={{
							dataIndex: 'userGroupId',
							title: 'User Group',
							dataType: 'multiSelect',
						}}
						filterModel={filterModel}
						onFilter={handleFilter}
						onClose={() => closeFilterDropdown('userGroupId')}
						visible={filterDropdowns.userGroupId}
						multiSelectOptions={userGroups.map((group) => ({
							label: group.name,
							value: group.id,
						}))}
						setFilterDropdown={setFilterDropdowns}
					/>
				),
				filterIcon: (filtered) => (
					<FilterOutlined
						style={{ color: filtered ? '#1890ff' : undefined }}
						onClick={() => toggleFilterDropdown('userGroupId')}
					/>
				),
			},
			{
				title: 'Department',
				dataIndex: 'departmentId',
				key: 'department',
				width: 120,
				sortDirections: ['ascend', 'descend'],
				sorter: true,
				dataType: 'multiSelect',
				ellipsis: {
					showTitle: false,
				},
				render: (departmentId) => {
					const department = departments.find(
						(dept) => dept.id === departmentId
					);
					return (
						<Tooltip
							placement="topLeft"
							title={department ? department.name : departmentId}
						>
							{department ? department.name : 'Not Assigned'}
						</Tooltip>
					);
				},
				filterDropdown: ({
					setSelectedKeys,
					selectedKeys,
					confirm,
					clearFilters,
				}) => (
					<CustomFilterDropdown
						column={{
							dataIndex: 'department',
							title: 'Department',
							dataType: 'multiSelect',
						}}
						filterModel={filterModel}
						onFilter={handleFilter}
						onClose={() => closeFilterDropdown('department')}
						visible={filterDropdowns.department}
						multiSelectOptions={departments.map((dept) => ({
							label: dept.name,
							value: dept.id,
						}))}
						setFilterDropdown={setFilterDropdowns}
					/>
				),
				filterIcon: (filtered) => (
					<FilterOutlined
						style={{ color: filtered ? '#1890ff' : undefined }}
						onClick={() => toggleFilterDropdown('department')}
					/>
				),
			},
			{
				title: 'Status',
				dataIndex: 'active',
				key: 'active',
				width: 50,
				sortDirections: ['ascend', 'descend'],
				sorter: true,
				dataType: 'boolean',
				render: (active) => (
					//<Tag color={active ? 'oklch(0.5307 0.1791 144.1)' : 'oklch(0.6144 0.1809 25.93)'}>
					<Tag color={active ? 'green' : 'red'}>
						{active ? 'Active' : 'Inactive'}
					</Tag>
				),
				filterDropdown: ({
					setSelectedKeys,
					selectedKeys,
					confirm,
					clearFilters,
				}) => (
					<CustomFilterDropdown
						column={{
							dataIndex: 'active',
							title: 'Status',
							dataType: 'boolean',
						}}
						filterModel={filterModel}
						onFilter={handleFilter}
						onClose={() => closeFilterDropdown('active')}
						visible={filterDropdowns.active}
						setFilterDropdown={setFilterDropdowns}
					/>
				),
				filterIcon: (filtered) => (
					<FilterOutlined
						style={{ color: filtered ? '#1890ff' : undefined }}
						onClick={() => toggleFilterDropdown('active')}
					/>
				),
			},
			{
				title: 'Date Created',
				dataIndex: 'dateCreated',
				key: 'dateCreated',
				width: 100,
				sortDirections: ['ascend', 'descend'],
				sorter: true,
				dataType: 'date',
				render: (date) => dayjs(date).format('MM/DD/YYYY'),
				filterDropdown: ({
					setSelectedKeys,
					selectedKeys,
					confirm,
					clearFilters,
				}) => (
					<CustomFilterDropdown
						column={{
							dataIndex: 'dateCreated',
							title: 'Date Created',
							dataType: 'date',
						}}
						filterModel={filterModel}
						onFilter={handleFilter}
						onClose={() => closeFilterDropdown('dateCreated')}
						visible={filterDropdowns.dateCreated}
						setFilterDropdown={setFilterDropdowns}
					/>
				),
				filterIcon: (filtered) => (
					<FilterOutlined
						style={{ color: filtered ? '#1890ff' : undefined }}
						onClick={() => toggleFilterDropdown('dateCreated')}
					/>
				),
			},
			// {
			//     title: 'Location',
			//     dataIndex: 'location',
			//     key: 'location',
			//     //width: 150,
			//     render: (location, record) => {
			//         const isRemote = record.locationObject?.isRemote;
			//         return <Text>{isRemote ? `🌐 ${location}` : location}</Text>;
			//     },
			// },
			{
				title: 'Employee ID',
				dataIndex: 'employeeId',
				key: 'employeeId',
				width: 50,
				sortDirections: ['ascend', 'descend'],
				sorter: true,
				dataType: 'text',
				ellipsis: {
					showTitle: false,
				},
				render: (employeeId) => (
					<Tooltip placement="topLeft" title={employeeId}>
						{employeeId}
					</Tooltip>
				),
				filterDropdown: ({
					setSelectedKeys,
					selectedKeys,
					confirm,
					clearFilters,
				}) => (
					<CustomFilterDropdown
						column={{
							dataIndex: 'employeeId',
							title: 'Employee ID',
							dataType: 'text',
						}}
						filterModel={filterModel}
						onFilter={handleFilter}
						onClose={() => closeFilterDropdown('employeeId')}
						visible={filterDropdowns.employeeId}
						setFilterDropdown={setFilterDropdowns}
					/>
				),
				filterIcon: (filtered) => (
					<FilterOutlined
						style={{ color: filtered ? '#1890ff' : undefined }}
						onClick={() => toggleFilterDropdown('employeeId')}
					/>
				),
			},
			{
				title: 'Budget',
				dataIndex: 'budget',
				key: 'budget',
				width: 80,
				render: (_, record) => {
					const recognitionEvents = record?.additionalInfo?.recognitionEvents;
					let employeeBudget =
						recognitionEvents?.currentBudget ??
						currentUser?.company?.recognitionSettings?.baseSnappyBudget ??
						0;
					if (recognitionEvents?.additionalBudgetTotal > 0) {
						employeeBudget += recognitionEvents?.additionalBudgetTotal;
					}
					if (recognitionEvents?.amountPending > 0) {
						employeeBudget -= recognitionEvents?.amountPending;
					}
					return (
						<Text
							style={{ color: 'oklch(0.5307 0.1791 144.1)', fontWeight: 900 }}
						>
							${employeeBudget || '0'}
						</Text>
					);
				},
			},
		],
		[
			userGroups,
			departments,
			currentUser,
			filterModel,
			filterDropdowns,
			handleOpenModal,
			handleFilter,
			toggleFilterDropdown,
			closeFilterDropdown,
		]
	);

	return (
		<div>
			<div
				style={{
					marginBottom: 10,
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
				}}
			>
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
						gap: 12,
					}}
				>
					<span
						style={{
							marginRight: 12,
							fontSize: 14,
							fontWeight: 800,
							color: '#212121',
						}}
					>
						{totalRecords !== undefined
							? `${totalRecords} records`
							: 'Loading records…'}
					</span>
					<AnimatedExportButton
						onExportClick={onExportClick}
						exportLoading={exportLoading}
						percentageLoaded={percentageLoaded}
						disabled={exportLoading || data?.length === 0}
					/>
				</div>
			</div>

			{/* Virtualized Table */}
			<ResizeObserver
				onResize={({ width, height }) => {
					setTableWidth(width);
					setTableHeight(Math.min(height || 600, 600));
				}}
			>
				<div
					style={{
						border: '1px solid #ddd',
						borderRadius: '16px',
						overflow: 'hidden',
						backgroundColor: '#fff',
					}}
				>
					<VirtualTableHeader />

					{loading && data.length === 0 ? (
						<div
							style={{
								height: 400,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: '16px',
								color: '#595959',
							}}
						>
							Loading employees...
						</div>
					) : data.length === 0 ? (
						<div
							style={{
								height: 400,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: '16px',
								color: '#595959',
							}}
						>
							No employees found
						</div>
					) : (
						<List
							ref={listRef}
							height={600}
							itemCount={data.length}
							itemSize={ROW_HEIGHT}
							itemData={data}
							width="100%"
							overscanCount={10}
							key={JSON.stringify(columnWidths)}
							style={{
								position: 'relative',
								height: '600px',
								width: '100%',
								overflow: 'auto',
								willChange: 'transform',
								direction: 'ltr',
								backfaceVisibility: 'hidden',
								transform: 'translateZ(0px)',
								scrollBehavior: 'smooth',
							}}
						>
							{VirtualizedRow}
						</List>
					)}

					{/* Pagination-style loading section */}
					<div
						style={{
							height: '32px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							backgroundColor: '#fff',
							borderTop: '1px solid rgb(240, 240, 240)',
							fontSize: '14px',
							color: '#595959',
							padding: '32px 0',
						}}
					>
						{loading && data.length > 0 ? (
							<Typography.Text type="secondary">
								Loading more records...
							</Typography.Text>
						) : !hasMoreData && data.length > 0 ? (
							<Typography.Text type="secondary">
								Showing all {data.length} of {totalRecords} records
							</Typography.Text>
						) : data.length === 0 && !loading ? (
							<Typography.Text type="secondary">
								No records to display
							</Typography.Text>
						) : (
							<Typography.Text type="secondary">
								{data.length} of {totalRecords} records loaded
							</Typography.Text>
						)}
					</div>
				</div>
			</ResizeObserver>

			{/* Employee Details Modal */}
			{selectedEmployeeId && (
				<EmployeeDetailsModalContainer
					visible={modalVisible}
					employeeId={selectedEmployeeId}
					onCancel={handleCloseModal}
					client={client}
					currentUser={currentUser}
					departments={departments}
					userGroups={userGroups}
				/>
			)}
		</div>
	);
};

export default EmployeeGridAntD;
